import { AxiosError } from 'axios';
import { DEFAULT_SERVICE_ERROR, getProblemMessage } from '.';
import { get, post } from './sp-api.service';
import { OrgPolicyEngine } from '../models/org.model';

export type PolicyConversionMode = 'dry-run' | 'apply';

// matched splits policies where needed to keep each user's result; one-to-one keeps one policy per policy.
export type PolicyConversionRoute = 'matched' | 'one-to-one';

export type ConversionIssueSeverity = 'blocking' | 'warning' | 'info';

export type ConversionIssue = {
  severity: ConversionIssueSeverity;
  code: string;
  message: string;
  policyId?: string;
  policyName?: string;
  affectedUserCount?: number;
  recentUserCount?: number;
};

export type ConvertedPolicyRole =
  | 'override'
  | 'org-default'
  | 'data-controls'
  | 'blocked-services'
  | 'blocked-tools'
  | 'monitored-services'
  | 'allowed-services'
  | 'group';

export type ConvertedPolicy = {
  role?: ConvertedPolicyRole;
  sourcePolicyId?: string;
  sourcePolicyName?: string;
  name: string;
  priority?: number;
};

export type ConversionIssueType = {
  code: string;
  severity: ConversionIssueSeverity;
  title: string;
  description: string;
};

export type ConversionImpact = {
  userCount: number;
  policyUserCounts: Record<string, number>;
  usage?: { windowDays: number; available: boolean };
};

export type PolicyConversionReport = {
  route?: PolicyConversionRoute;
  blocking: boolean;
  v1PolicyCount: number;
  v2PolicyCount: number;
  existingV2PolicyCount: number;
  requiredEngineSettings: OrgPolicyEngine;
  issues: ConversionIssue[];
  impact?: ConversionImpact;
};

export type PolicyConversionResult = {
  ok: boolean;
  status: number;
  policies: ConvertedPolicy[];
  report?: PolicyConversionReport;
  error?: string;
};

type ConversionBody = {
  policies?: {
    role?: ConvertedPolicyRole;
    sourcePolicyId?: string;
    sourcePolicyName?: string;
    policy?: { name?: string; priority?: number };
    name?: string;
    priority?: number;
  }[];
  report?: PolicyConversionReport;
};

const ConversionErrors: Record<number, string> = {
  403: 'Only a root user can convert policies',
  404: 'The organization has no v1 organization default policy',
  409: 'The organization runs policy engine v2. Switch it back to v1 before you convert again.',
  422: 'The report has a blocking issue, so the conversion was not applied',
};

const NoRouteError =
  'The API gateway did not send the request to the policies service (404). Its route for POST /policies/v2-conversion may be missing.';

const conversionErrorMessage = (error: AxiosError): string => {
  const status = error.response?.status || 0;
  const body = (error.response?.data || {}) as { detail?: unknown };

  // The service's own 404 has a generic `detail`, and missing the v1 org default is its only 404. A 404 with no `detail` comes from the API gateway.
  if (status === 404) {
    return body.detail ? ConversionErrors[404] : NoRouteError;
  }

  return getProblemMessage(error, ConversionErrors[status]);
};

// A dry run returns planned policies as { role, sourcePolicyId, policy }; a real run returns the stored policies.
const toConvertedPolicies = (body: ConversionBody): ConvertedPolicy[] =>
  (body.policies || []).map(
    ({ role, sourcePolicyId, sourcePolicyName, policy, name, priority }) => ({
      role,
      sourcePolicyId: sourcePolicyId || undefined,
      sourcePolicyName: sourcePolicyName || undefined,
      name: policy?.name || name || '',
      priority: policy?.priority ?? priority,
    })
  );

export const getConversionIssueTypes = async (): Promise<ConversionIssueType[]> => {
  const response = (await get('/policies/v2-conversion/issue-types')) as {
    data?: ConversionIssueType[];
  } | null;

  return response?.data || [];
};

// Root only. A dry run writes nothing. A real run replaces the org's v2 policies; the v1 policies stay.
export const convertPoliciesToV2 = async (
  orgId: string,
  mode: PolicyConversionMode,
  route: PolicyConversionRoute = 'matched'
): Promise<PolicyConversionResult> => {
  const onError = <T>(error: AxiosError): T | null => {
    const status = error.response?.status || 0;
    const body = (error.response?.data || {}) as ConversionBody;

    return {
      ok: false,
      status,
      policies: toConvertedPolicies(body),
      report: body.report,
      error: conversionErrorMessage(error),
    } as T;
  };

  const response = await post<ConversionBody | PolicyConversionResult>(
    '/policies/v2-conversion',
    // matched is the API default; leaving it out keeps a dry run working on an API without routes
    { orgId, mode, ...(route !== 'matched' && { route }) },
    onError
  );

  if (!response) {
    return { ok: false, status: 0, policies: [], error: DEFAULT_SERVICE_ERROR };
  }

  if ('ok' in response) {
    return response;
  }

  return {
    ok: true,
    status: 200,
    policies: toConvertedPolicies(response),
    report: response.report,
  };
};
